/* eslint-disable prefer-destructuring */
/* eslint-disable no-console */
import { ThemeProvider } from '@digital-retail/design-system';
import { Favicons, Provider, ThemeContext } from '@digital-retail/falabella-ui-cross-components';
import {
  getLatLongFromAddressLine,
  getSeoPageTitle,
  isSodimacRetail,
  isSodimacStandalone,
  isTottusStandalone,
  isWhitelabel,
} from '@digital-retail/store-manager';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import constants from '../config/constants';
import LayoutContainer from '../containers/Layout/LayoutContainer';
import loadDigitalData from '../external_dependencies/digitalData';
import loadExternalDependencies from '../external_dependencies/index';
import { ApplicationProvider, withApplicationContext } from '../utils/ApplicationContext';
import { CookieStorage } from '../utils/CookieStorage';
import '../utils/CoreJsImports';
import { getTheme } from '../utils/DesignSystemTheme';
import { DigitalDataProvider } from '../utils/DigitalDataContext';
import { fetchApplicationData } from '../utils/FetchData';
import { LabelProvider } from '../utils/LabelsContext';
import _ from '../utils/LodashImports';
import Logger, { attachErrorLogger } from '../utils/Logger';
import { MyListProvider } from '../utils/MyListsContext';
import { redirectToUrl } from '../utils/RouteHandler';
import { UserProvider } from '../utils/UserContext';
import WithUrl from '../utils/WithUrl';
import { getDeviceType, getPlatformType, isInternetExplorer, isSearchBot, isWindow } from '../utils/deviceChecker';
import { parseAllZones, parseZones, validateFields } from '../utils/tottus.helper';
import { getStoreValue } from '../utils/urlHelper';

const LayoutWithAppContext = withApplicationContext(LayoutContainer);

const nextConfig = getConfig();
const publicRuntimeConfig = (nextConfig && nextConfig.publicRuntimeConfig) || {};
const serverRuntimeConfig = (nextConfig && nextConfig.serverRuntimeConfig) || {};

const isComponentRenderer = (Component) => {
  return Component.name === 'ComponentRenderer' || Component.name === 'ComponentStylesRenderer';
};
const calculateThemes = (store, initialAppCtx = {}) => {
  if (initialAppCtx.disableSISTheme) {
    return undefined;
  }
  if (isWhitelabel({ store })) {
    return _.get(initialAppCtx, 'header.entry.pdp_themes', undefined);
  }
  return _.get(initialAppCtx, 'header.entry.themes', undefined);
};

// comment to prevent CC CSS caching
const App = ({ Component, pageProps, appCtx: initialAppCtx = {} }) => {
  const router = useRouter();
  attachErrorLogger();
  const currentSIS = initialAppCtx?.store || 'marketplace';
  const [themeState, setThemeState] = useState(calculateThemes(currentSIS, initialAppCtx));
  const rebrandingModifier = initialAppCtx?.siteConfig?.textDictionary?.REBRANDING_MODIFIER || 'solar';
  const skipExternalScripts = router.query?.skipExternalScripts === 'true';
  useEffect(() => {
    const themes = calculateThemes(currentSIS, initialAppCtx);
    setThemeState(themes);
    if (!skipExternalScripts) {
      loadExternalDependencies();
    }
    Logger.init(initialAppCtx);

    const zonesArray = Array.isArray(initialAppCtx.zones) ? initialAppCtx.zones : [];

    CookieStorage.setCookie({
      name: 'priceGroupId',
      value: _.get(initialAppCtx, 'priceGroupId', ''),
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    CookieStorage.setCookie({
      name: 'politicalId',
      value: _.get(initialAppCtx, 'politicalId', ''),
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    CookieStorage.setCookie({
      name: 'zones',
      value: JSON.stringify(zonesArray),
      regionCode: initialAppCtx.regionCode,
      store: initialAppCtx.store,
    });
    if (isTottusStandalone(initialAppCtx.store)) {
      const expressZones = _.get(initialAppCtx, 'expressZones', '').split(',');
      const standardZones = _.get(initialAppCtx, 'standardZones', '').split(',');
      CookieStorage.setCookie({
        name: 'zonesList',
        value: btoa(JSON.stringify({ expressZones, standardZones })),
        regionCode: initialAppCtx.regionCode,
        store: initialAppCtx.store,
      });
    }

    const comuna = _.get(initialAppCtx, 'comuna', {});
    const isDefaultComuna = _.get(initialAppCtx, 'isDefaultComuna', false);
    if (comuna.default && isDefaultComuna) {
      CookieStorage.setCookie({
        name: 'IS_ZONE_SELECTED',
        value: false,
        regionCode: initialAppCtx.regionCode,
        store: initialAppCtx.store,
      });
      CookieStorage.setCookie({
        name: 'userSelectedZone',
        value: 'defaultZone',
        regionCode: initialAppCtx.regionCode,
        store: initialAppCtx.store,
      });
    }
    const COOKIES_PAIR_TO_REMOVE = _.get(initialAppCtx, 'siteConfig.textDictionary.COOKIES_PAIR_TO_REMOVE', '');

    CookieStorage.removeCookiePairs(COOKIES_PAIR_TO_REMOVE, initialAppCtx.regionCode, initialAppCtx.store);
  }, []);

  useEffect(() => {
    loadDigitalData(router.route);
  }, [router.route]);

  const commerceOriginData = { name: 'PDP', view: 'Product' };
  const [CCThemeProvider] = ThemeContext;

  return (
    <DigitalDataProvider>
      <ApplicationProvider appCtx={initialAppCtx}>
        <Provider
          tenant={initialAppCtx.regionCode}
          baseUrl={_.get(
            initialAppCtx,
            'header.entry.header_configs[0].links.catalystbaseurl',
            'https://www.falabella.com'
          )}
          deviceType={initialAppCtx.deviceType}
          enableOldStoreInStoreHeaders={initialAppCtx.disableSISTheme}
          commerceOriginData={commerceOriginData}
          isSodimacStandAloneSite={initialAppCtx.isSodimacSA || initialAppCtx.isSodimacRE}
          isTottusStandAloneSite={initialAppCtx.isTottusSA}
        >
          <LabelProvider labels={(initialAppCtx.siteConfig && initialAppCtx.siteConfig.textDictionary) || {}}>
            <Head>
              <title>{getSeoPageTitle({ store: initialAppCtx.store })}</title>
              <meta name="viewport" content="initial-scale=1.0, width=device-width, maximum-scale=1,user-scalable=no" />
              <meta name="theme-color" content="#FFFFFF" />
              <meta charset="iso-8859-1" />
              <meta name="HandheldFriendly" content="True" />
              <meta name="MobileOptimized" content="320" />
              <meta name="format-detection" content="telephone=no" />
              <meta name="apple-mobile-web-app-status-bar-style" content="black" />
              <meta httpEquiv="pragma" content="cache" />
              <meta property="og:type" content="article" />
              <meta name="apple-mobile-web-app-title" content={getSeoPageTitle({ store: initialAppCtx.store })} />
              <meta name="language" content="ES" />
              <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
              <meta name="google-site-verification" content="1VMve7ONbnxKqYZ1xuoXrvmrAEs8rMkCFsIfcNUp_Cs" />
              {Favicons.render({
                tenant: initialAppCtx.regionCode,
                store: initialAppCtx.store,
              })}
              <link rel="preconnect" href="https://gateway.foresee.com" />
              <link rel="preconnect" href="https://analytics.foresee.com" />
              <link rel="preconnect" href="https://falabella.demdex.net" crossOrigin />
              <link rel="preconnect" href="https://connect.facebook.net" crossOrigin />
              <link rel="preconnect" href="https://www.facebook.com" crossOrigin />
              <link rel="preconnect" href="https://stats.g.doubleclick.net" crossOrigin />
            </Head>
            <UserProvider>
              <LayoutWithAppContext>
                <MyListProvider appCtx={initialAppCtx}>
                  <CCThemeProvider themes={themeState} store={currentSIS}>
                    <ThemeProvider theme={getTheme(rebrandingModifier)}>
                      <Component {...pageProps} />
                    </ThemeProvider>
                  </CCThemeProvider>
                </MyListProvider>
              </LayoutWithAppContext>
            </UserProvider>
          </LabelProvider>
        </Provider>
      </ApplicationProvider>
    </DigitalDataProvider>
  );
};

App.getInitialProps = async ({ Component, router, ctx }) => {
  try {
    if (!ctx.query.site) {
      const url = WithUrl(404);
      redirectToUrl(url, ctx.res);
      return {};
    }
    const isClientSide = isWindow();
    const cookie = isClientSide ? document.cookie : ctx.req.headers.cookie;
    const cookies = CookieStorage.parseCookies(cookie);
    const { headers } = ctx.req || {};
    const reqUrl = isClientSide ? window.location.pathname + window.location.search : ctx.req.url;
    const { host } = headers || {};
    const buildId = (headers && headers['x-next-build-id']) || '';
    const appCtx = {
      subdomain: ctx.query?.subdomain,
      tenant: ctx.query?.site || '',
      host,
      isComponentRequest: isComponentRenderer(Component),
      deviceType: !isClientSide ? getDeviceType(ctx.req.headers) : getDeviceType(),
      isInternetExplorer: !isClientSide
        ? isInternetExplorer(ctx.req.headers['user-agent'])
        : isInternetExplorer(navigator.userAgent),
      busy: 0,
      buildId,
      ssrCookies: cookie,
      tracingHeaders: {},
      isSearchBot: !isClientSide && ctx.req.headers['user-agent'] && isSearchBot(ctx.req.headers['user-agent']),
      platformType: !isClientSide ? getPlatformType(ctx.req.headers) : getPlatformType(window.navigator.userAgent),
      url: reqUrl,
    };
    appCtx.regionCode = `${appCtx.tenant.substr(appCtx.tenant.indexOf('-') + 1, 2)}`;
    appCtx.store = (ctx?.query?.store && getStoreValue(ctx.query.store)) || null;

    const isTottusSA = isTottusStandalone(appCtx.store);
    const isSodimacSA = isSodimacStandalone(appCtx.store);
    const isSodimacRE = isSodimacRetail(appCtx.store);

    appCtx.isTottusSA = isTottusSA;
    appCtx.isSodimacSA = isSodimacSA;
    appCtx.isKiosk = cookies?.KIOSKO_STORE_ID ? cookies.KIOSKO_STORE_ID !== '' : false;
    appCtx.isSodimacRE = isSodimacRE;

    Logger.init(appCtx);
    const getHost = (env, tenant, url, store) => {
      if (tenant === 'cl') {
        return `https://${env}.falabella.com${url}`;
      }
      if (isSodimacRetail(store)) {
        return `https://${env}.sodimac.com.${tenant}${url}`;
      }
      return `https://${env}.falabella.com.${tenant}${url}`;
    };

    publicRuntimeConfig.API_HOST = getHost(
      publicRuntimeConfig.env,
      appCtx.regionCode,
      publicRuntimeConfig.BROWSE_ENDPOINT,
      appCtx.store
    );

    publicRuntimeConfig.CUSTOMER_LOCATION_HOST = getHost(
      publicRuntimeConfig.env,
      appCtx.regionCode,
      publicRuntimeConfig.CL_ENDPOINT,
      appCtx.store
    );

    publicRuntimeConfig.CONTENT_API_HOST = getHost(
      publicRuntimeConfig.env,
      appCtx.regionCode,
      publicRuntimeConfig.CONTENT_ENDPOINT,
      appCtx.store
    );

    const { env, CLUSTER_API_HOST, CLUSTER_CUSTOMER_LOCATION_HOST, CLUSTER_CONTENT_API_HOST, ENVIRONMENT } =
      publicRuntimeConfig;
    appCtx.environment = ENVIRONMENT || 'production';
    if (env === 'local') {
      publicRuntimeConfig.API_HOST = CLUSTER_API_HOST;
      publicRuntimeConfig.CUSTOMER_LOCATION_HOST = CLUSTER_CUSTOMER_LOCATION_HOST;
      publicRuntimeConfig.CONTENT_API_HOST = CLUSTER_CONTENT_API_HOST;
    }

    if (!isClientSide && ctx.res && !(ctx.res.finished || ctx.res.headersSent)) {
      constants.TRACING_HEADERS.forEach((headerName) => {
        if (headers && headers[headerName]) {
          ctx.res.setHeader(headerName, headers[headerName]);
          appCtx.tracingHeaders[headerName] = headers[headerName];
        }
      });
    }

    const isvalid = (data) => data && data !== 'undefined' && data !== 'false' && data !== '';

    const regionId = ctx?.query?.region_id?.replace(/_/g, '-');

    if (isvalid(regionId)) {
      appCtx.pid = regionId;
    } else {
      appCtx.pid = isvalid(cookies.politicalId) ? cookies.politicalId : 'default';
    }

    if (isTottusSA) {
      const addressLines = isvalid(cookies.addressLines) ? cookies.addressLines : '';
      const latLng = getLatLongFromAddressLine(addressLines);
      appCtx.latLong = latLng;
    }

    const isUserSelectedZone = cookies.userSelectedZone === 'userselected';

    appCtx.isDefaultComuna = cookies.userSelectedZone === 'defaultZone';

    const cookieList = ['zonesList', 'priceGroupId', 'politicalId'];
    const isValidCookies = validateFields(isvalid)(cookieList);
    const appDataFetcher = fetchApplicationData(publicRuntimeConfig, serverRuntimeConfig, appCtx, ctx.query);

    let services = [];
    const zoneCookie = parseZones(isvalid)(cookies.zones);
    const hasValidZones = zoneCookie?.filter(Boolean).length > 0;

    if (!isClientSide) {
      services = ['siteConfig', 'transversalBanner', 'header', 'footer', 'taxonomy', 'sisNavigationMenu'];
      if (!isValidCookies(cookies)) {
        services.push('comuna');
      }
      if (appCtx.store && !services.includes('sisNavigationMenu')) {
        services.push('sisNavigationMenu');
      }
      if (isSodimacRE && !hasValidZones && cookies.politicalId) {
        services.push('virtualZones');
      }
    }

    const servicesData = await appDataFetcher.fetch(services);

    services.forEach((service, i) => {
      appCtx[service] = servicesData[i];
    });

    appCtx.isNewZoneFeatureEnabled = appCtx?.siteConfig?.toggles?.isNewZoneFeatureEnabled ?? false;

    if (!isClientSide && appCtx.siteConfig && !_.get(appCtx, 'siteConfig.toggles.isPoliticalIdExists', false)) {
      const service = ['zonesInfo'];
      const serviceData = await appDataFetcher.fetch(service);
      const [zonesInfo] = serviceData;
      appCtx.zonesInfo = zonesInfo;
    }

    if (!isClientSide && appCtx.siteConfig) {
      appCtx.siteConfig.textDictionary.ITEM_ADDED_IN_CART = _.get(
        appCtx,
        'siteConfig.textDictionary.ITEM_ADDED_IN_CART_MKP',
        'Lo que llevas en tu Carro'
      );
    }

    appCtx.isOrangeHeader = _.get(cookies, 'ORANGE_COLOR_AB', '') === 'true';
    appCtx.isSISLevel2Header = _.get(cookies, 'SISLEVEL2_HEADER_AB', '') === 'true';

    const configureDefaultZones = async (contextObj, cookieValues, parserZonesFn) => {
      const tempZones = parserZonesFn(isvalid)(cookies.zones);

      const fallbackValues = {
        priceGroupId: cookieValues.priceGroupId,
        politicalId: cookieValues.politicalId,
        zones: tempZones,
        isDefaultComuna: false,
      };

      try {
        const comunaResponse = await contextObj.comuna;
        const virtualZonesResponse = hasValidZones ? zoneCookie : contextObj?.virtualZones?.data;
        const comuna = _.get(comunaResponse, 'data');

        if (!comuna) {
          return fallbackValues;
        }

        const zones = Array.isArray(comuna.zones) ? comuna.zones : [];
        const updatedValues = {
          priceGroupId: comuna.priceGroupId,
          politicalId: comuna.politicalId,
          zones: isSodimacRE ? virtualZonesResponse : zones.join(),
          isDefaultComuna: _.get(comunaResponse, 'default', false) ? true : !isUserSelectedZone,
        };

        if (isTottusSA) {
          const expressZones = Array.isArray(comuna.expressZones) ? comuna.expressZones : [];
          const standardZones = Array.isArray(comuna.standardZones) ? comuna.standardZones : [];

          updatedValues.expressZones = expressZones.join();
          updatedValues.standardZones = standardZones.join();
        }

        return updatedValues;
      } catch (error) {
        return fallbackValues;
      }
    };

    if (isTottusSA && isValidCookies(cookies)) {
      const allZones = parseAllZones(cookies, appCtx, isvalid, Logger);
      const { expressZones, standardZones } = allZones;

      Object.assign(appCtx, {
        expressZones: expressZones.join(),
        standardZones: standardZones.join(),
        priceGroupId: cookies.priceGroupId,
        politicalId: cookies.politicalId,
        zones: parseZones(isvalid)(cookies.zones),
      });
    } else {
      const updatedValues = await configureDefaultZones(appCtx, cookies, parseZones);

      Object.assign(appCtx, updatedValues);
    }

    const themeCookie = cookies.EXP_SIS || cookies.exp_sis;
    appCtx.disableSISTheme = themeCookie === 'old';

    appCtx.isVwoEnabled = publicRuntimeConfig.VWO_ENABLED === 'true';

    let pageProps = {};

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({ config: publicRuntimeConfig }, ctx, appCtx, router);
    }

    return {
      pageProps,
      appCtx,
    };
  } catch (err) {
    const tenant = ctx.query.site;
    Logger.error(`error in _app.js ${err} - ${err?.stack}`);
    Logger.error(`error in _app.js router - ${JSON.stringify(router)}`);
    Logger.error(
      `error in _app.js ctx - ${JSON.stringify({
        asPath: ctx.asPath,
        query: ctx.query,
      })}`
    );
    const url = constants.APP_COMP_ERR_PAGES[tenant];
    redirectToUrl(url, ctx.res);
    return {};
  }
};

App.propTypes = {
  pageProps: PropTypes.object,
  Component: PropTypes.func,
  appCtx: PropTypes.object.isRequired,
};

App.defaultProps = {
  pageProps: {},
  // eslint-disable-next-line prettier/prettier
  Component: () => {},
};

export default App;
